import React, { Suspense, lazy } from 'react';
import api from './services/api';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { EntitlementProvider } from './contexts/EntitlementContext';
import { ComplianceProvider } from './ComplianceContext';

// ── EAGER (app shell + auth) ────────────────────────────────────────
// Only what's needed to paint the frame and log in. Everything routed
// below is lazy so the first load doesn't parse the whole app.
import EntitlementGuard from './components/EntitlementGuard';
import Unauthorized from './components/Unauthorized';
import Login from './components/Login';
import TenantLogin from './components/TenantLogin';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import ErrorBoundary from './components/ErrorBoundary';
import SessionTimeoutModal from './components/SessionTimeoutModal';
import SetupPassword from './components/SetupPassword';
import ChangePassword from './components/ChangePassword';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';

// ── LAZY (route pages) — Wave 3 (perf): each route is its own chunk. ─
// Heavy libs (tiptap, exceljs, jspdf, docx-preview, recharts, pagedjs)
// ride along into the chunks that use them instead of the main bundle.
const Workspaces = lazy(() => import('./components/Workspaces'));
const ComplianceDashboard = lazy(() => import('./components/ComplianceDashboard'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const CopilotChat = lazy(() => import('./components/CopilotChat'));
const FrameworkDetail = lazy(() => import('./components/FrameworkDetail'));
const ControlsListing = lazy(() => import('./components/ControlsListing'));
const TestsListingPage = lazy(() => import('./components/TestsListingPage'));
const LogAuditing = lazy(() => import('./pages/LogAuditing'));
const ConnectorLogs = lazy(() => import('./components/ConnectorLogs'));
const AuditLog = lazy(() => import('./components/AuditLog'));
const TestDetailPage = lazy(() => import('./components/TestDetailPage'));
const PersonnelTasksPage = lazy(() => import('./components/PersonnelTasksPage'));
const SystemEffectiveness = lazy(() => import('./components/SystemEffectiveness'));
const ReportsDashboard = lazy(() => import('./components/ReportsDashboard'));
const ReportsHub = lazy(() => import('./components/ReportsHub'));
const ReportViewer = lazy(() => import('./components/reports/ReportViewer'));
const CISODashboard = lazy(() => import('./components/CISODashboard'));
const StatementOfApplicability = lazy(() => import('./components/StatementOfApplicability'));
const AuditorPortalV2 = lazy(() => import('./components/AuditorPortal/AuditorPortalV2'));
const AuditorLanding = lazy(() => import('./components/AuditorPortal/AuditorLanding'));
const PoliciesDashboard = lazy(() => import('./components/PoliciesDashboard'));
const PolicyDetail = lazy(() => import('./components/PolicyDetail'));
const PrintLayout = lazy(() => import('./components/PrintLayout'));
const DocumentVault = lazy(() => import('./components/DocumentVault'));
const DSRQueue = lazy(() => import('./components/DSRQueue'));
const ConsentManager = lazy(() => import('./components/ConsentManager'));
const VendorDPATracker = lazy(() => import('./components/VendorDPATracker'));
const RoPALedger = lazy(() => import('./components/RoPALedger'));
const PIAWorkspace = lazy(() => import('./components/PIAWorkspace'));
const SettingsLayout = lazy(() => import('./components/Settings/SettingsLayout'));
const SettingsForm = lazy(() => import('./components/Settings/SettingsForm'));
const SmtpSettings = lazy(() => import('./components/Settings/SmtpSettings'));
const ReminderSettings = lazy(() => import('./components/Settings/ReminderSettings'));
const StorageSettings = lazy(() => import('./components/Settings/StorageSettings'));
const MfaSettings = lazy(() => import('./components/Settings/MfaSettings'));
const AngelaTraces = lazy(() => import('./components/Settings/AngelaTraces'));
const ContextPage = lazy(() => import('./components/ContextPage'));
const UserManagement = lazy(() => import('./components/UserManagement'));
const AdminInbox = lazy(() => import('./components/AdminInbox'));
const AuditRequestsInbox = lazy(() => import('./components/AuditRequestsInbox'));
const ApprovalTasks = lazy(() => import('./components/ApprovalTasks'));
const ProvisioningWizard = lazy(() => import('./components/ProvisioningWizard'));
const TisaxReview = lazy(() => import('./components/TisaxReview'));
const TisaxAttributionPage = lazy(() => import('./components/TisaxAttribution'));
const TisaxScoring = lazy(() => import('./components/TisaxScoring'));
const PeopleDirectory = lazy(() => import('./components/PeopleDirectory'));
const IdentityRiskDashboard = lazy(() => import('./components/IdentityRiskDashboard'));
const NCTrackingLog = lazy(() => import('./components/NCTrackingLog'));
// Internal Audit Lifecycle (Phase 2) — superadmin-gated until the tenant
// entitlement gate exists; server enforces the same gate on every endpoint.
const InternalAuditHome = lazy(() => import('./components/InternalAudit/InternalAuditHome'));
const IAProgrammeDetail = lazy(() => import('./components/InternalAudit/ProgrammeDetail'));
const IAAuditWorkspace = lazy(() => import('./components/InternalAudit/AuditWorkspace'));
const IAManagementReview = lazy(() => import('./components/InternalAudit/ManagementReview'));
const IAProcedurePage = lazy(() => import('./components/InternalAudit/ProcedurePage'));
const IAProcessesPage = lazy(() => import('./components/InternalAudit/ProcessesPage'));
// P7c (2026-10-10) — record performed organisational activities (org_audits writer).
const OrgActivitiesPage = lazy(() => import('./components/OrgActivities/OrgActivitiesPage'));
const SupportAccessLog = lazy(() => import('./components/SupportAccess/SupportAccessLog'));
const RolesGuide = lazy(() => import('./components/Help/RolesGuide'));
const RiskGovernanceDocs = lazy(() => import('./components/Risk/RiskGovernanceDocs'));
const WorkspaceSelector = lazy(() => import('./components/WorkspaceSelector'));
const IntegrationsHub = lazy(() => import('./components/Integrations/IntegrationsHub'));
const ConnectorConfig = lazy(() => import('./components/Integrations/ConnectorConfig'));
const SSOSettings = lazy(() => import('./components/Settings/SSOSettings'));
const RiskHub = lazy(() => import('./components/Risk/RiskHub'));
const RiskRegister = lazy(() => import('./components/Risk/RiskRegister'));
const RiskDetail = lazy(() => import('./components/Risk/RiskDetail'));
const RiskHeatmap = lazy(() => import('./components/Risk/RiskHeatmap'));
const AssetRegister = lazy(() => import('./components/Risk/AssetRegister'));
const AISystemInventory = lazy(() => import('./components/Risk/AISystemInventory'));
const MaturityAssessment = lazy(() => import('./components/Risk/MaturityAssessment'));
const RiskConfig = lazy(() => import('./components/Risk/RiskConfig'));
const RiskMethodology = lazy(() => import('./components/Risk/RiskMethodology'));
const AssetDiscovery = lazy(() => import('./components/Risk/AssetDiscovery'));
const RiskLibrary = lazy(() => import('./components/Risk/RiskLibrary'));
const TenantRiskProfile = lazy(() => import('./components/Risk/TenantRiskProfile'));
const ApprovalDashboard = lazy(() => import('./components/Risk/ApprovalDashboard'));
const OnboardingAgent = lazy(() => import('./components/Risk/OnboardingAgent'));
const AgentReviewCommit = lazy(() => import('./components/Risk/AgentReviewCommit'));
// VRM Deploy 3.3 — Vendor Risk Management module
const VendorList = lazy(() => import('./components/Vendors/VendorList'));
const VendorApprovalQueue = lazy(() => import('./components/Vendors/VendorApprovalQueue'));
const VendorForm = lazy(() => import('./components/Vendors/VendorForm'));
const VendorDetail = lazy(() => import('./components/Vendors/VendorDetail'));
const CmmcSupplyChainLedger = lazy(() => import('./components/Vendors/CmmcSupplyChainLedger'));
// VRM Deploy 3.4 — framework-aware Methodology page
const VendorMethodology = lazy(() => import('./components/Vendors/VendorMethodology'));
const VendorAssessmentPortal = lazy(() => import('./components/Vendors/VendorAssessmentPortal'));
// Angella Questionnaire Filler — inbound security questionnaires
const QuestionnaireList = lazy(() => import('./components/Questionnaires/QuestionnaireList'));
const QuestionnaireDetail = lazy(() => import('./components/Questionnaires/QuestionnaireDetail'));
const SMEQueue = lazy(() => import('./components/Questionnaires/SMEQueue'));
const QuestionnaireSettings = lazy(() => import('./components/Questionnaires/QuestionnaireSettings'));
// Tier 1 (Vik 2026-11-02): retention policy setup at onboarding (full page, package 5 2.16)
const RetentionPolicySetup = lazy(() => import('./components/RetentionPolicySetup'));

// Chunk-load fallback — light page-shaped shimmer, shown only while a route's
// JS chunk downloads (typically once per route per deploy, then browser-cached).
const PageFallback = () => (
    <div className="p-6 space-y-4 animate-fade-in">
        <div className="h-7 w-56 bg-gray-100 rounded animate-pulse" />
        <div className="h-4 w-96 bg-gray-100 rounded animate-pulse" />
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-3">
            {[...Array(6)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-100 rounded animate-pulse" style={{ width: `${90 - i * 9}%` }} />
            ))}
        </div>
    </div>
);

// --- PLACEHOLDER COMPONENT FOR NEW ROUTES ---
const PlaceholderPage = ({ title }) => (
    <div className="p-8 animate-fade-in">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{title}</h1>
        <div className="bg-white p-16 rounded-xl border border-gray-200 shadow-sm text-center">
            <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6">
                <span className="text-3xl">🚧</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Under Construction</h3>
            <p className="text-gray-500 max-w-md mx-auto">
                The <b>{title}</b> module is linked and ready for development.
                Detailed views for this section will be implemented in the next phase.
            </p>
        </div>
    </div>
);

const ProtectedRoute = ({ children, allowedRoles = [], requireSuperuser = false }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return <div className="flex h-screen items-center justify-center text-gray-500">Loading verification...</div>;
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Superadmin-only screens (global console, provisioning). Gate on the
    // is_superuser flag itself — role strings like 'admin' also exist on
    // tenant users, so they are NOT sufficient for cross-tenant screens.
    if (requireSuperuser && !user.is_superuser) {
        return <Navigate to="/unauthorized" replace />;
    }

    // Force password change before accessing any protected page
    if (user?.must_change_password && window.location.pathname !== '/change-password') {
        return <Navigate to="/change-password" replace />;
    }

    // Role-Based Access Control
    if (user) {
        const role = user.role || 'admin'; // Default to admin if missing
        // Role model (owner 2026-10-10)
        const isInternalAuditor = role === 'auditor' && (user.auditor_type || '') === 'internal';
        const tenantSlugMatch = window.location.pathname.match(/^\/t\/([^/]+)/);

        // 0. Employees: My tasks only.
        if (role === 'employee') {
            const inner = tenantSlugMatch ? window.location.pathname.slice(tenantSlugMatch[0].length) : window.location.pathname;
            const ok = ['/my-tasks', '/change-password'].some(p => inner === p || inner.startsWith(p + '/'));
            if (!ok) {
                return <Navigate to={tenantSlugMatch ? `${tenantSlugMatch[0]}/my-tasks` : '/change-password'} replace />;
            }
        }

        // 1. Auditors get read-only access to a fixed allowlist of
        //    paths in addition to /auditor-portal. 2026-04-27 user
        //    spec: auditor sees Effectiveness, Compliance Overview,
        //    Tests, Document Vault, Risk Methodology, CISO Dashboard,
        //    Reports Hub. Components themselves render in read-only
        //    mode based on the role flag.
        if (role === 'auditor' && !isInternalAuditor) {
            const path = window.location.pathname;
            // Owner 2026-10-10: external auditors use the Auditor Portal only. Effectiveness,
            // Compliance Overview, Reports and the other organisation-wide pages are blocked;
            // their content appears as dated evidence records under in-scope controls.
            const auditorAllowedPaths = [
                '/auditor-portal',
                '/change-password',
            ];
            // tenant-scoped variants like /t/{slug}/effectiveness
            const tenantPathMatch = path.match(/^\/t\/[^/]+(\/.*)?$/);
            const innerPath = tenantPathMatch ? (tenantPathMatch[1] || '') : path;
            const allowed = auditorAllowedPaths.some((p) =>
                innerPath === p || innerPath.startsWith(p + '/')
            );
            if (!allowed) {
                return <Navigate to="/auditor-portal/dashboard" replace />;
            }
        }

        // 2. Strict Role Check (if allowedRoles prop provided)
        if (allowedRoles.length > 0 && !allowedRoles.includes(role)
            && !(isInternalAuditor && allowedRoles.includes('internal_auditor'))) {
            // Context aware return for unauthorized might be needed, but for now simple:
            return <Navigate to="/unauthorized" replace />;
        }
    }

    return children;
};

const PendingApprovalPopup = ({ count, tenantId, onClose }) => {
    if (count <= 0) return null;
    return (
        <div className="fixed inset-0 bg-black/40 z-[200] flex items-center justify-center animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5">
                    <h3 className="text-white font-bold text-lg">📋 Pending Approvals</h3>
                    <p className="text-blue-100 text-sm mt-1">Documents are waiting for your review</p>
                </div>
                <div className="p-6">
                    <div className="flex items-center gap-4 mb-5">
                        <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                            <span className="text-2xl font-bold text-blue-600">{count}</span>
                        </div>
                        <div>
                            <p className="text-gray-900 font-semibold">
                                {count === 1 ? '1 document needs' : `${count} documents need`} your approval
                            </p>
                            <p className="text-gray-500 text-sm">As an assigned approver, timely review ensures compliance.</p>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={() => { onClose(); window.location.href = `/t/${tenantId}/approval-tasks`; }}
                            className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Review Now
                        </button>
                        <button
                            onClick={onClose}
                            className="px-4 py-2.5 text-gray-600 font-medium text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Later
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ProtectedLayout = () => {
    const { user } = useAuth();
    const tenantId = window.location.pathname.match(/\/t\/([^/]+)/)?.[1];
    const [pendingCount, setPendingCount] = React.useState(0);
    const [showPopup, setShowPopup] = React.useState(false);
    const [masqueradeInfo, setMasqueradeInfo] = React.useState(null);

    React.useEffect(() => {
        const checkSession = async () => {
            if (!tenantId || !user) return;

            try {
                // Call Health Check
                await api.get('/health/session-check', {
                    params: { tenant_id: tenantId },
                    headers: { 'X-Target-Tenant-ID': tenantId }
                });
            } catch (err) {
                console.error("Session integrity failed:", err);
                window.location.href = `/login`;
            }
        };

        checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantId]);

    // Masquerading banner: check if superadmin is impersonating
    React.useEffect(() => {
        const checkMasquerade = async () => {
            if (!tenantId || !user) return;
            const isImpersonating = !!localStorage.getItem('admin_token') && user?.is_superuser;
            if (!isImpersonating) {
                setMasqueradeInfo(null);
                return;
            }
            try {
                const res = await api.get('/auth/masquerade-info');
                if (res.data?.is_masquerading) {
                    setMasqueradeInfo(res.data);
                }
            } catch (err) {
                // Fallback: show banner with tenantId from URL
                setMasqueradeInfo({ is_masquerading: true, target_tenant_name: tenantId });
            }
        };
        checkMasquerade();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantId, user?.is_superuser]);

    // Login popup: check for pending approvals once on mount
    React.useEffect(() => {
        const checkPendingApprovals = async () => {
            if (!tenantId || !user) return;
            const popupKey = `approval_popup_shown_${tenantId}_${user.username}`;
            if (sessionStorage.getItem(popupKey)) return;

            try {
                const res = await api.get('/vault/pending-tasks/count');
                const count = res.data?.count || 0;
                if (count > 0) {
                    setPendingCount(count);
                    setShowPopup(true);
                    sessionStorage.setItem(popupKey, 'true');
                }
            } catch (err) {
                // Non-critical
            }
        };

        const timer = setTimeout(checkPendingApprovals, 1500);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantId, user?.username]);

    const handleReturnToConsole = async () => {
        // A1: end support access — writes the audit END row and restores the admin session.
        try { await api.post('/auth/impersonate/end'); } catch (e) { /* not in a session */ }
        localStorage.removeItem('admin_token');
        window.location.href = '/super-admin';
    };

    // Mobile drawer state (sidebar is always visible at lg+)
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    React.useEffect(() => {
        // Close the drawer if user resizes to desktop while it was open.
        const onResize = () => { if (window.innerWidth >= 1024) setDrawerOpen(false); };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    return (
        <div className="flex h-screen bg-gray-50">
            <Sidebar mobileOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />
            <div className="flex-1 flex flex-col overflow-auto lg:ml-52 xl:ml-56 2xl:ml-64">
                {/* Mobile top bar — hidden on lg+ */}
                <div className="lg:hidden flex-shrink-0 flex items-center justify-between bg-gradient-to-br from-sky-500 via-sky-200 to-sky-50 border-b border-sky-100 px-4 py-2.5 z-30">
                    <button
                        onClick={() => setDrawerOpen(true)}
                        className="text-slate-700 p-2 -ml-2 rounded hover:bg-white/40"
                        aria-label="Open menu"
                    >
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="3" y1="6" x2="21" y2="6"/>
                            <line x1="3" y1="12" x2="21" y2="12"/>
                            <line x1="3" y1="18" x2="21" y2="18"/>
                        </svg>
                    </button>
                    <div className="flex items-center select-none">
                        <img src="/assurisk-logo.svg" alt="AssuRisk" className="h-9 w-auto" draggable={false} />
                    </div>
                    <div className="w-9" /> {/* spacer to balance hamburger */}
                </div>

                {/* Masquerading Banner — always visible when impersonating */}
                {masqueradeInfo?.is_masquerading && (
                    <div className="flex-shrink-0 bg-amber-500 text-black px-4 py-2 flex items-center justify-between text-sm font-semibold z-50 shadow-md">
                        <div className="flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <span>
                                {masqueradeInfo.read_only === false
                                    ? <>Support access (own tenant, full access): <strong>{masqueradeInfo.target_tenant_name || tenantId}</strong>
                                        {' '}&mdash; changes are saved and audit-logged under your own identity</>
                                    : <>Support access (read-only): <strong>{masqueradeInfo.target_tenant_name || tenantId}</strong>
                                        {' '}&mdash; changes cannot be made in this tenant</>}
                            </span>
                        </div>
                        <button
                            onClick={handleReturnToConsole}
                            className="px-3 py-1 bg-black/20 hover:bg-black/30 rounded text-xs font-bold transition-colors"
                        >
                            Exit to Global Console
                        </button>
                    </div>
                )}
                {/* Desktop top bar — always rendered (incl. admin/masquerade view),
                    so the bell/status are never bypassed. Mobile keeps its own strip. */}
                <TopBar />
                <div className="flex-1 overflow-auto">
                    {/* Wave 3 (perf): route chunks load inside the shell — the
                        sidebar/topbar never unmount while a chunk downloads. */}
                    <Suspense fallback={<PageFallback />}>
                        <Outlet />
                    </Suspense>
                </div>
            </div>
            {showPopup && (
                <PendingApprovalPopup
                    count={pendingCount}
                    tenantId={tenantId}
                    onClose={() => setShowPopup(false)}
                />
            )}
        </div>
    );
};

function App() {
    return (
        <ErrorBoundary>
            <ComplianceProvider>
            <AuthProvider>
                <Router>
                    <EntitlementProvider>
                        {/* Top-level boundary for lazy routes OUTSIDE the tenant
                            layout (auditor portal, print, workspace selector). */}
                        <Suspense fallback={<PageFallback />}>
                        <Routes>
                            {/* VRM Deploy 3.7 — Public vendor portal route.
                                NO authentication; token in URL is the credential.
                                Must be registered BEFORE any auth-gated routes. */}
                            <Route
                                path="/vendor-assessment/:token"
                                element={<VendorAssessmentPortal />}
                            />

                            <Route path="/login" element={<Login />} />

                            <Route path="/login" element={<Login />} />

                            {/* MAGIC LINK LOGIN */}
                            <Route path="/t/:tenantId/login" element={<TenantLogin />} />



                            {/* SUPER ADMIN WORKSPACES — cross-tenant console, superadmin only */}
                            <Route
                                path="/super-admin"
                                element={
                                    <ProtectedRoute requireSuperuser>
                                        <Workspaces />
                                    </ProtectedRoute>
                                }
                            />
                           <Route
    				path="/super-admin/onboarding"
    				element={
        				<ProtectedRoute requireSuperuser>
            					<ProvisioningWizard />
        				</ProtectedRoute>
    				}
			   />

                            <Route
                                path="/super-admin/support-access"
                                element={
                                    <ProtectedRoute requireSuperuser>
                                        <SupportAccessLog scope="platform" />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/super-admin/tisax-review"
                                element={
                                    <ProtectedRoute requireSuperuser>
                                        <TisaxReview />
                                    </ProtectedRoute>
                                }
                            />
                            {/* Public-facing (any authenticated user) VDA ISA
                                attribution + licence + modification marking. */}
                            <Route
                                path="/tisax/attribution"
                                element={
                                    <ProtectedRoute>
                                        <TisaxAttributionPage />
                                    </ProtectedRoute>
                                }
                            />

                            {/* RETENTION POLICY SETUP (No Sidebar), package 5 2.16: the Admin confirms or changes each class. */}
                            <Route
                                path="/t/:tenantId/retention-setup"
                                element={
                                    <ProtectedRoute>
                                        <RetentionPolicySetup />
                                    </ProtectedRoute>
                                }
                            />

                            {/* TENANT CONTEXT LAYOUT */}
                            <Route
                                path="/t/:tenantId"
                                element={
                                    <ProtectedRoute>
                                        <ProtectedLayout />
                                    </ProtectedRoute>
                                }
                            >
                                {/* DASHBOARD */}
                                <Route path="dashboard" element={<Dashboard />} />
                                {/* COMPLIANCE COPILOT (GRC chat agent) */}
                                <Route path="copilot" element={<CopilotChat />} />

                                {/* ANGELLA — Security Questionnaire Filler (inbound) */}
                                <Route path="questionnaires" element={<QuestionnaireList />} />
                                {/* Literal paths BEFORE :qid so they aren't matched as an id */}
                                <Route path="questionnaires/sme-queue" element={<SMEQueue />} />
                                <Route path="questionnaires/settings" element={<QuestionnaireSettings />} />
                                <Route path="questionnaires/:qid" element={<QuestionnaireDetail />} />

                                {/* COMPLIANCE DASHBOARD */}
                                <Route path="compliance-dashboard" element={<ComplianceDashboard />} />

                                {/* EFFECTIVENESS */}
                                <Route path="effectiveness" element={<SystemEffectiveness />} />
                                <Route path="activities" element={<OrgActivitiesPage />} />
                                <Route path="support-access" element={
                                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'tenant_admin', 'org_admin']}>
                                        <SupportAccessLog scope="tenant" />
                                    </ProtectedRoute>
                                } />

                                {/* FRAMEWORKS */}
                                <Route path="frameworks" element={<Navigate to="../dashboard" replace />} />
                                <Route path="frameworks/:id" element={
                                    <EntitlementGuard>
                                        <FrameworkDetail />
                                    </EntitlementGuard>
                                } />

                                {/* OVERVIEW */}
                                <Route path="controls" element={<ControlsListing />} />
                                <Route path="tisax-scoring" element={<TisaxScoring />} />
                                <Route path="monitors" element={
                                    <EntitlementGuard resource="feature" requiredId="aws_scanner">
                                        <PlaceholderPage title="Monitors" />
                                    </EntitlementGuard>
                                } />
                                {/* Workstream 5 — Vanta-style Tests pages */}
                                <Route path="tests" element={<TestsListingPage />} />
                                <Route path="tests/:testId" element={<TestDetailPage />} />
                                {/* Deploy 2.48 — Log Auditing */}
                                <Route path="log-auditing" element={<LogAuditing />} />
                                {/* Deploy 2.66 — read-only connector audit trail + security audit log */}
                                <Route path="connector-logs" element={<ConnectorLogs />} />
                                <Route path="audit-log" element={<AuditLog />} />
                                {/* Phase C — auditee inbox for auditor evidence requests */}
                                <Route path="audit-requests" element={<AuditRequestsInbox />} />
                                <Route path="my-tasks" element={<PersonnelTasksPage />} />
                                {/* ISO 27701 Pillar 3 — Data Subject Rights queue */}
                                <Route path="dsr" element={<DSRQueue />} />
                                <Route path="consent" element={<ConsentManager />} />
                                <Route path="vendor-dpa" element={<VendorDPATracker />} />
                                <Route path="ropa" element={<RoPALedger />} />
                                <Route path="pia" element={<PIAWorkspace />} />
                                <Route path="get-started" element={<PlaceholderPage title="Get Started" />} />


                                {/* DOCUMENTS — Stage 9.5: /documents and /evidence redirect to /vault */}
                                <Route path="documents" element={<Navigate to="../vault" replace />} />
                                <Route path="evidence" element={<Navigate to="../vault" replace />} />
                                <Route path="vault" element={<DocumentVault />} />
                                <Route path="policies" element={
                                    <EntitlementGuard resource="feature" requiredId="policy_management">
                                        <PoliciesDashboard />
                                    </EntitlementGuard>
                                } />
                                <Route path="policies/:id" element={<PolicyDetail />} />
                                <Route path="risk" element={
                                    <EntitlementGuard resource="feature" requiredId="risk_management">
                                        <RiskHub />
                                    </EntitlementGuard>
                                } />
                                <Route path="risk/register" element={<RiskRegister />} />
                                <Route path="risk/detail/:riskId" element={<RiskDetail />} />
                                <Route path="risk/heatmap" element={<RiskHeatmap />} />
                                <Route path="risk/assets" element={<AssetRegister />} />
                                <Route path="risk/ai-systems" element={<AISystemInventory />} />
                                <Route path="risk/maturity" element={<MaturityAssessment />} />
                                <Route path="risk/config" element={
                                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'tenant_admin']}>
                                        <RiskConfig />
                                    </ProtectedRoute>
                                } />
                                <Route path="risk/methodology" element={<RiskMethodology />} />
                                {/* Owner 2026-10-10: acceptance criteria record + versioned RTP */}
                                <Route path="risk/acceptance-and-plan" element={<RiskGovernanceDocs />} />
                                <Route path="risk/discovery" element={<AssetDiscovery />} />
                                <Route path="risk/library" element={<RiskLibrary />} />
                                <Route path="risk/profile" element={
                                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'tenant_admin']}>
                                        <TenantRiskProfile />
                                    </ProtectedRoute>
                                } />
                                <Route path="risk/agent/onboarding" element={
                                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'tenant_admin']}>
                                        <OnboardingAgent />
                                    </ProtectedRoute>
                                } />
                                <Route path="risk/agent/review" element={
                                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'tenant_admin']}>
                                        <AgentReviewCommit />
                                    </ProtectedRoute>
                                } />
                                <Route path="risk/approval" element={<ApprovalDashboard />} />
                                {/* VRM Deploy 3.3 — Vendor Risk Management module */}
                                <Route path="vendors" element={
                                    <EntitlementGuard resource="feature" requiredId="vendor_management">
                                        <VendorList />
                                    </EntitlementGuard>
                                } />
                                <Route path="vendors/new" element={
                                    <EntitlementGuard resource="feature" requiredId="vendor_management">
                                        <VendorForm />
                                    </EntitlementGuard>
                                } />
                                {/* VRM Deploy 3.4 — Methodology page. Registered BEFORE
                                    /vendors/:vendorId so the literal path 'methodology'
                                    doesn't get matched as a UUID. */}
                                <Route path="vendors/methodology" element={
                                    <EntitlementGuard resource="feature" requiredId="vendor_management">
                                        <VendorMethodology />
                                    </EntitlementGuard>
                                } />
                                {/* VRM Deploy 3.8 — Admin approval queue. Registered
                                    BEFORE /vendors/:vendorId so 'approval-queue'
                                    isn't matched as a UUID. */}
                                <Route path="vendors/approval-queue" element={
                                    <EntitlementGuard resource="feature" requiredId="vendor_management">
                                        <VendorApprovalQueue />
                                    </EntitlementGuard>
                                } />
                                {/* CMMC Supply Chain Control Ledger — registered BEFORE
                                    /vendors/:vendorId so the literal path isn't matched
                                    as a vendor UUID. */}
                                <Route path="vendors/cmmc-supply-chain" element={
                                    <EntitlementGuard resource="feature" requiredId="vendor_management">
                                        <CmmcSupplyChainLedger />
                                    </EntitlementGuard>
                                } />
                                <Route path="vendors/:vendorId" element={
                                    <EntitlementGuard resource="feature" requiredId="vendor_management">
                                        <VendorDetail />
                                    </EntitlementGuard>
                                } />

                                {/* GOVERNANCE */}
                                <Route path="governance/context" element={<ContextPage />} />

                                {/* SETTINGS MODULE */}
                                <Route path="settings" element={
                                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'tenant_admin', 'org_admin']}>
                                        <SettingsLayout />
                                    </ProtectedRoute>
                                }>
                                    <Route path="smtp" element={<SmtpSettings />} />
                                    <Route path="storage" element={<StorageSettings />} />
                                    <Route path="sso" element={<SSOSettings />} />
                                    <Route path="reminders" element={<ReminderSettings />} />
                                    <Route path="security_mfa" element={<MfaSettings />} />
                                    <Route path="angela_activity" element={<AngelaTraces />} />
                                    <Route path="retention" element={<RetentionPolicySetup />} />
                                    <Route path=":section" element={<SettingsForm />} />
                                </Route>

                                {/* ACCOUNT — per-user security (2FA), open to ALL authenticated users */}
                                <Route path="account/security" element={
                                    <ProtectedRoute>
                                        <div className="p-8 max-w-4xl mx-auto"><MfaSettings /></div>
                                    </ProtectedRoute>
                                } />

                                {/* REPORTS */}
                                <Route path="reports" element={<ReportsHub />} />
                                <Route path="reports/ciso-dashboard" element={<CISODashboard />} />
                                <Route path="reports/:reportSlug" element={<ReportViewer />} />
                                <Route path="trust-report" element={<ReportsHub />} />
                                <Route path="legacy-reports" element={<ReportsDashboard />} />

                                {/* MANAGE */}
                                <Route path="people" element={
                                    <EntitlementGuard resource="feature" requiredId="people_management">
                                        <PeopleDirectory />
                                    </EntitlementGuard>
                                } />
                                <Route path="people/risks" element={<IdentityRiskDashboard />} />
                                <Route path="groups" element={<PlaceholderPage title="Groups" />} />
                                <Route path="computers" element={<PlaceholderPage title="Computers" />} />
                                <Route path="checklists" element={<PlaceholderPage title="Checklists" />} />
                                <Route path="access" element={<PlaceholderPage title="Access" />} />
                                <Route path="access-reviews" element={<PlaceholderPage title="Access Reviews" />} />

                                {/* SOA PREVIEW — visible to all authenticated roles (read-only for editor/viewer) */}
                                <Route path="soa-preview" element={<StatementOfApplicability />} />

                                {/* AUDITOR PORTAL (Waitlist link in Sidebar) */}
                                <Route path="auditor-portal" element={<AuditorLanding />} />
                                <Route path="auditor-portal/dashboard" element={<AuditorPortalV2 />} />

                                {/* NC TRACKING LOG — Centralized NC Register */}
                                <Route path="nc-log" element={<NCTrackingLog />} />

                                {/* INTERNAL AUDIT (ISO 19011 / cl. 9.2+9.3) — distinct from
                                    the external Auditor Portal. Superadmin-gated at route,
                                    nav AND server layers until the tenant entitlement gate
                                    exists (Phase 2 ruling, 2026-08-19). Literal path before
                                    the parameterized siblings. */}
                                <Route path="internal-audit" element={
                                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'tenant_admin', 'org_admin', 'internal_auditor']}>
                                        <InternalAuditHome />
                                    </ProtectedRoute>
                                } />
                                <Route path="internal-audit/management-review" element={
                                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'tenant_admin', 'org_admin', 'internal_auditor']}>
                                        <IAManagementReview />
                                    </ProtectedRoute>
                                } />
                                {/* Item 1 (2026-08-21) — standalone 9.2.2 procedure document */}
                                <Route path="internal-audit/procedure" element={
                                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'tenant_admin', 'org_admin', 'internal_auditor']}>
                                        <IAProcedurePage />
                                    </ProtectedRoute>
                                } />
                                {/* R3 (2026-08-21) — tenant-authored process registry */}
                                <Route path="internal-audit/processes" element={
                                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'tenant_admin', 'org_admin', 'internal_auditor']}>
                                        <IAProcessesPage />
                                    </ProtectedRoute>
                                } />
                                <Route path="internal-audit/programmes/:programmeId" element={
                                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'tenant_admin', 'org_admin', 'internal_auditor']}>
                                        <IAProgrammeDetail />
                                    </ProtectedRoute>
                                } />
                                <Route path="internal-audit/audits/:auditId" element={
                                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'tenant_admin', 'org_admin', 'internal_auditor']}>
                                        <IAAuditWorkspace />
                                    </ProtectedRoute>
                                } />

                                {/* INTEGRATIONS */}
                                <Route path="integrations" element={<IntegrationsHub />} />
                                {/* P3-C4: Storage under Integrations — reuses the
                                    existing StorageSettings component so Settings →
                                    Data Storage continues to work unchanged. */}
                                <Route path="integrations/storage" element={<StorageSettings />} />
                                <Route path="integrations/:connectorType" element={<ConnectorConfig />} />

                                {/* Role model 2026-10-10: Roles & permissions help page */}
                                <Route path="admin/roles-guide" element={<RolesGuide />} />
                                {/* ADMIN USER MANAGEMENT */}
                                <Route path="admin/users" element={
                                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'tenant_admin', 'org_admin']}>
                                        <UserManagement />
                                    </ProtectedRoute>
                                } />

                                {/* APPROVAL TASKS — Document approval inbox */}
                                <Route path="approval-tasks" element={<ApprovalTasks />} />

                                {/* ADMIN INBOX */}
                                <Route path="admin/inbox" element={<AdminInbox />} />
                            </Route>

                            {/* PRINT LAYOUT (No Sidebar) */}
                            <Route
                                path="/print/policy/:id"
                                element={
                                    <ProtectedRoute>
                                        <PrintLayout />
                                    </ProtectedRoute>
                                }
                            />

                            {/* AUDITOR PORTAL (No Sidebar) */}
                            <Route
                                path="/auditor-portal"
                                element={
                                    <ProtectedRoute>
                                        <AuditorLanding />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/auditor-portal/dashboard"
                                element={
                                    <ProtectedRoute>
                                        <AuditorPortalV2 />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/auditor-portal/v2"
                                element={
                                    <ProtectedRoute>
                                        <AuditorPortalV2 />
                                    </ProtectedRoute>
                                }
                            />

                            {/* WORKSPACE SELECTOR — SSO multi-tenant */}
                            <Route path="/select-workspace" element={<WorkspaceSelector />} />

                            {/* SETUP PASSWORD — Public, no auth required */}
                            <Route path="/setup-password" element={<SetupPassword />} />
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password" element={<ResetPassword />} />

                            {/* CHANGE PASSWORD — Authenticated, forced on first login */}
                            <Route path="/change-password" element={<ChangePassword />} />

                            {/* UNAUTHORIZED PAGE */}
                            <Route path="/unauthorized" element={<Unauthorized />} />

                            {/* FALLBACK */}
                            <Route path="/" element={<Navigate to="/login" replace />} />
                            <Route path="*" element={<Navigate to="/login" replace />} />
                        </Routes>
                        </Suspense>
                        <SessionTimeoutModal />
                    </EntitlementProvider>
                </Router>
            </AuthProvider>
            </ComplianceProvider>
        </ErrorBoundary >
    );
}

export default App;
